import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Card, 
  CardHeader, 
  CardTitle, 
  CardContent 
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '../components/ui/dialog';
import {
  BookOpen,
  Download,
  FileText,
  User,
  Building,
  Heart,
  Globe,
  Eye,
  Calendar,
  FileDown,
  Info,
  RefreshCw,
  ArrowLeft,
  Languages,
  Sparkles,
  CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';
import {
  AdminGuideButtons,
  NewGuideButton,
  AdminDeliverableButtons,
  NewDeliverableButton,
} from './UserGuidesAdminActions';


// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Documentation Centre — official project deliverables (STRATIS)
// Versions and dates are baked-in here so reviewers see consistent metadata
// no matter when the page is rendered.
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const DELIVERABLES = [
  {
    id: 'implementation-study',
    titleEl: 'Μελέτη Εφαρμογής',
    titleEn: 'Implementation Study (Functional Specifications)',
    description:
      'Προ-αναπτυξιακή μελέτη με όλες τις λειτουργικές και τεχνικές προδιαγραφές των 16 modules της πλατφόρμας.',
    version: '2.1',
    lastUpdated: 'Apr 2026',
    fileEl: 'ISISP_Implementation_Study.docx',
    fileEn: 'ISISP_Implementation_Study_EN.docx',
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-700',
  },
  {
    id: 'platform-completion',
    titleEl: 'Ολοκλήρωση Πλατφόρμας Κοινωνικών Επενδύσεων',
    titleEn: 'Platform Completion & Hand-over Plan',
    description:
      'Σχέδιο ολοκλήρωσης μετά την ανάπτυξη: integration testing, πιλοτική, UAT, security, deployment, SROI baseline, financial reporting, βιωσιμότητα, αναμενόμενα αποτελέσματα.',
    version: '1.1',
    lastUpdated: 'Apr 2026',
    fileEl: 'ISISP_Platform_Completion.docx',
    fileEn: 'ISISP_Platform_Completion_EN.docx',
    iconBg: 'bg-indigo-100',
    iconColor: 'text-indigo-700',
  },
  {
    id: 'development-documentation',
    titleEl: 'Πλήρης Τεχνική Τεκμηρίωση Ανάπτυξης',
    titleEn: 'Full Development Documentation',
    description:
      'Live-generated αναφορά της παραγωγικής υλοποίησης με screenshots, API endpoints, data models και workflow walkthroughs.',
    version: 'rolling',
    lastUpdated: 'auto',
    fileEl: 'ISISP_Development_Documentation_EL.docx',
    fileEn: 'ISISP_Development_Documentation.docx',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-700',
  },
];


const UserGuides = () => {
  const [guides, setGuides] = useState([]);
  const [deliverables, setDeliverables] = useState([]);
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [guideContent, setGuideContent] = useState('');
  const [guideLang, setGuideLang] = useState('en'); // language of the currently-open guide
  const [showGuideDialog, setShowGuideDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [refreshingDoc, setRefreshingDoc] = useState(false);
  const [translatingGuides, setTranslatingGuides] = useState({}); // { [guide_id]: true }
  const [syncingStale, setSyncingStale] = useState(false);
  const [syncProgress, setSyncProgress] = useState(null); // {current, queue, completed, failed}
  
  const user = JSON.parse(localStorage.getItem('user')) || {};

  useEffect(() => {
    fetchGuides();
    fetchDeliverables();
    // Pick up any in-flight sync-stale job (admin only)
    const isAdmin = JSON.parse(localStorage.getItem('user') || '{}')?.role === 'administrator';
    if (isAdmin) {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      axios
        .get(`${BACKEND_URL}/api/user-guides/sync-stale-greek/status`, { headers })
        .then(({ data }) => {
          if (data?.state === 'running') {
            // re-attach polling
            syncStaleGreek();
          }
        })
        .catch(() => {});
    }
  }, []);

  const downloadDeliverable = (filenameOrUrl, label) => {
    // URLs starting with /api/ are streamed by the backend (uploaded admin
    // files). Other strings are filenames in /app/frontend/public served as
    // static assets (the original STRATIS deliverables).
    const isApi = (filenameOrUrl || '').startsWith('/api/');
    const a = document.createElement('a');
    if (isApi) {
      // Backend FileResponse — append the auth token via a fetch+blob flow.
      fetch(`${BACKEND_URL}${filenameOrUrl}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      })
        .then((r) => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.blob();
        })
        .then((blob) => {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = (filenameOrUrl.split('/').pop() || 'document') + '';
          document.body.appendChild(link);
          link.click();
          link.remove();
          URL.revokeObjectURL(url);
          toast.success(`Λήψη: ${label}`);
        })
        .catch((e) => toast.error(`Download failed: ${e.message}`));
      return;
    }
    a.href = `${BACKEND_URL}/${filenameOrUrl}`;
    a.download = filenameOrUrl;
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success(`Λήψη: ${label}`);
  };

  const fetchGuides = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/user-guides`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setGuides(response.data);
    } catch (error) {
      console.error('Error fetching guides:', error);
      toast.error('Failed to load user guides');
    } finally {
      setLoading(false);
    }
  };

  const fetchDeliverables = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/documentation-deliverables`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setDeliverables(response.data || []);
    } catch (error) {
      console.error('Error fetching deliverables:', error);
      // Soft fail — keep the page usable even if the deliverables list 404s.
      setDeliverables([]);
    }
  };

  const openGuide = async (guide, lang = 'en') => {
    try {
      setSelectedGuide(guide);
      setGuideLang(lang);
      const response = await axios.get(`${BACKEND_URL}/api/user-guides/${guide.id}?lang=${lang}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setGuideContent(response.data.content);
      setShowGuideDialog(true);
    } catch (error) {
      console.error('Error loading guide:', error);
      toast.error(error?.response?.data?.detail || 'Failed to load guide content');
    }
  };

  const downloadGuide = async (guide, format = 'markdown', lang = 'en') => {
    try {
      setDownloading(guide.id);
      const response = await axios.get(
        `${BACKEND_URL}/api/user-guides/${guide.id}/download?format=${format}&lang=${lang}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          responseType: 'blob'
        }
      );

      const mimeByFormat = {
        txt: 'text/plain',
        markdown: 'text/markdown',
        html: 'text/html',
        docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        word: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      };
      const extByFormat = {
        txt: '.txt',
        markdown: '.md',
        html: '.html',
        docx: '.docx',
        word: '.docx',
      };
      const blob = new Blob([response.data], { type: mimeByFormat[format] || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const baseName = guide.filename.replace('.md', '');
      const langSuffix = lang === 'el' ? '_EL' : '';
      const ext = extByFormat[format] || '.md';
      link.download = `${baseName}${langSuffix}${ext}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Guide downloaded${lang === 'el' ? ' (Greek)' : ''}!`);
    } catch (error) {
      console.error('Error downloading guide:', error);
      toast.error(error?.response?.data?.detail || 'Failed to download guide');
    } finally {
      setDownloading(null);
    }
  };

  const translateGuideToGreek = async (guide) => {
    if (translatingGuides[guide.id]) return;
    try {
      setTranslatingGuides((prev) => ({ ...prev, [guide.id]: true }));
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      await axios.post(
        `${BACKEND_URL}/api/user-guides/${guide.id}/translate-greek`,
        {},
        { headers }
      );
      toast.info(`Translating "${guide.title}" into Greek… this may take 1-3 minutes.`);

      // Poll status every 5s for up to ~6 minutes
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts += 1;
        try {
          const { data } = await axios.get(
            `${BACKEND_URL}/api/user-guides/${guide.id}/translate-greek/status`,
            { headers }
          );
          if (data.state === 'success') {
            clearInterval(poll);
            setTranslatingGuides((prev) => {
              const next = { ...prev };
              delete next[guide.id];
              return next;
            });
            toast.success(data.message || 'Greek companion ready!');
            // Refresh registry so card shows the new EL badge / actions
            fetchGuides();
          } else if (data.state === 'error') {
            clearInterval(poll);
            setTranslatingGuides((prev) => {
              const next = { ...prev };
              delete next[guide.id];
              return next;
            });
            toast.error(data.message || 'Translation failed.');
          } else if (attempts >= 80) {
            clearInterval(poll);
            setTranslatingGuides((prev) => {
              const next = { ...prev };
              delete next[guide.id];
              return next;
            });
            toast.warning('Translation is taking longer than expected. Check back later.');
          }
        } catch (err) {
          clearInterval(poll);
          setTranslatingGuides((prev) => {
            const next = { ...prev };
            delete next[guide.id];
            return next;
          });
          toast.error('Lost connection to translation job.');
        }
      }, 5000);
    } catch (error) {
      setTranslatingGuides((prev) => {
        const next = { ...prev };
        delete next[guide.id];
        return next;
      });
      const msg = error?.response?.data?.detail || 'Failed to start Greek translation';
      toast.error(msg);
    }
  };

  const refreshFullDocumentation = async () => {
    if (refreshingDoc) return;
    try {
      setRefreshingDoc(true);
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      await axios.post(`${BACKEND_URL}/api/user-guides/refresh-documentation`, {}, { headers });
      toast.info('Refreshing documentation… this can take ~1-2 minutes.');

      // Poll status every 4s, max 60 attempts (~4 minutes)
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts += 1;
        try {
          const { data } = await axios.get(
            `${BACKEND_URL}/api/user-guides/refresh-documentation/status`,
            { headers }
          );
          if (data.state === 'success') {
            clearInterval(poll);
            setRefreshingDoc(false);
            toast.success(data.message || 'Documentation refreshed successfully.');
          } else if (data.state === 'error') {
            clearInterval(poll);
            setRefreshingDoc(false);
            toast.error(data.message || 'Documentation refresh failed.');
          } else if (attempts >= 60) {
            clearInterval(poll);
            setRefreshingDoc(false);
            toast.warning('Refresh is taking longer than expected. Check status later.');
          }
        } catch (err) {
          clearInterval(poll);
          setRefreshingDoc(false);
          toast.error('Lost connection to refresh job.');
        }
      }, 4000);
    } catch (error) {
      setRefreshingDoc(false);
      const msg = error?.response?.data?.detail || 'Failed to start refresh job';
      toast.error(msg);
    }
  };

  const syncStaleGreek = async () => {
    if (syncingStale) return;
    try {
      setSyncingStale(true);
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const startResp = await axios.post(
        `${BACKEND_URL}/api/user-guides/sync-stale-greek`,
        {},
        { headers }
      );
      if (startResp.data?.state === 'success') {
        setSyncingStale(false);
        toast.success(startResp.data.message || 'All Greek companions are up to date.');
        return;
      }
      const queueLen = (startResp.data?.queue || []).length;
      toast.info(`Syncing ${queueLen} Greek companion${queueLen === 1 ? '' : 's'} in the background…`);

      // Poll every 5s for up to ~10 minutes
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts += 1;
        try {
          const { data } = await axios.get(
            `${BACKEND_URL}/api/user-guides/sync-stale-greek/status`,
            { headers }
          );
          setSyncProgress({
            current: data.current,
            queue: data.queue,
            completed: data.completed,
            failed: data.failed,
          });
          if (data.state === 'success' || data.state === 'error') {
            clearInterval(poll);
            setSyncingStale(false);
            setSyncProgress(null);
            const okCount = (data.completed || []).length;
            const failCount = (data.failed || []).length;
            if (data.state === 'success' && failCount === 0) {
              toast.success(data.message || `${okCount} guide(s) synced to Greek.`);
            } else {
              toast.warning(`${okCount} synced • ${failCount} failed. ${data.message || ''}`);
            }
            fetchGuides();
          } else if (attempts >= 120) {
            clearInterval(poll);
            setSyncingStale(false);
            setSyncProgress(null);
            toast.warning('Sync is taking longer than expected. Check back later.');
          }
        } catch (err) {
          clearInterval(poll);
          setSyncingStale(false);
          setSyncProgress(null);
          toast.error('Lost connection to sync job.');
        }
      }, 5000);
    } catch (error) {
      setSyncingStale(false);
      const msg = error?.response?.data?.detail || 'Failed to start Greek sync';
      toast.error(msg);
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'administrator': return User;
      case 'investor': return Building;
      case 'social_actor': return Heart;
      default: return Globe;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'administrator': return 'text-blue-700 bg-blue-50';
      case 'investor': return 'text-green-700 bg-green-50';
      case 'social_actor': return 'text-purple-600 bg-purple-50';
      default: return 'text-gray-700 bg-gray-50';
    }
  };

  const isRelevantForUser = (guide) => {
    // Show role-specific guides and general guides
    return guide.role === 'all' || guide.role === user.role || user.role === 'administrator';
  };

  const formatMarkdownForDisplay = (markdown) => {
    // Simple markdown to HTML conversion for basic display
    return markdown
      .replace(/^### (.*$)/gim, '<h3 class="text-lg font-semibold mt-4 mb-2">$1</h3>')
      .replace(/^## (.*$)/gim, '<h2 class="text-xl font-semibold mt-6 mb-3">$1</h2>')
      .replace(/^# (.*$)/gim, '<h1 class="text-2xl font-bold mt-8 mb-4">$1</h1>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/`(.*?)`/g, '<code class="bg-gray-100 px-1 rounded">$1</code>')
      .replace(/\n/g, '<br>');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link 
            to="/dashboard" 
            className="flex items-center space-x-2 px-3 py-2 rounded-lg text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Back to Dashboard</span>
          </Link>
          <div className="border-l border-gray-300 h-8"></div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
              <BookOpen className="h-7 w-7 text-blue-700" aria-hidden="true" />
              <span>User Guides & Documentation</span>
            </h1>
            <p className="text-gray-700 mt-1">
              Comprehensive guides and documentation for effective platform usage
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {user.role === 'administrator' && (
            <>
              <Button
                data-testid="sync-stale-greek-btn"
                onClick={syncStaleGreek}
                disabled={syncingStale}
                variant="outline"
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                title="AI-translate any guide whose Greek companion is missing or out-of-date"
              >
                <Languages className={`h-4 w-4 mr-2 ${syncingStale ? 'animate-pulse' : ''}`} />
                {syncingStale
                  ? (syncProgress?.current
                      ? `Syncing EL… (${syncProgress.completed?.length || 0}/${(syncProgress.completed?.length || 0) + (syncProgress.queue?.length || 0)})`
                      : 'Syncing EL…')
                  : 'Sync Greek Companions'}
              </Button>
              <Button
                data-testid="refresh-documentation-btn"
                onClick={refreshFullDocumentation}
                disabled={refreshingDoc}
                variant="outline"
                className="border-blue-300 text-blue-700 hover:bg-blue-50"
                title="Re-capture screenshots and regenerate the development .docx (admin only)"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${refreshingDoc ? 'animate-spin' : ''}`} />
                {refreshingDoc ? 'Refreshing…' : 'Refresh'}
              </Button>
            </>
          )}
          <Button
            data-testid="download-implementation-study-btn"
            onClick={() => {
              document.querySelector('[data-testid="documentation-centre"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
            variant="outline"
            className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
            title="Jump to Documentation Centre — όλα τα παραδοτέα του έργου"
          >
            <FileDown className="h-4 w-4 mr-2" />
            Documentation Centre
          </Button>
        </div>
      </div>

      {/* ━━━━━━━━━━ Documentation Centre ━━━━━━━━━━ */}
      <Card className="border-slate-200 bg-gradient-to-br from-slate-50/60 to-white" data-testid="documentation-centre">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg">
                <FileText className="h-5 w-5 text-indigo-600" />
                Documentation Centre · Κέντρο Παραδοτέων
              </CardTitle>
              <p className="text-xs text-slate-700 mt-1">
                One-stop shop για όλα τα επίσημα παραδοτέα του έργου STRATIS — διαθέσιμα σε ελληνικά / αγγλικά.
              </p>
            </div>
            <div className="flex items-center gap-2">
              {user.role === 'administrator' && (
                <NewDeliverableButton onChanged={fetchDeliverables} />
              )}
              <span className="text-xs text-slate-700">
                {deliverables.length} deliverables
              </span>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {deliverables.map((d) => (
              <div
                key={d.id}
                className="border rounded-lg p-4 bg-white hover:shadow-md hover:border-indigo-300 transition-all flex flex-col"
                data-testid={`deliverable-${d.id}`}
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className={`rounded-lg p-2 ${d.icon_bg || 'bg-slate-100'} flex-shrink-0`}>
                    <FileText className={`h-5 w-5 ${d.icon_color || 'text-slate-700'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900 text-sm leading-snug">
                      {d.title_el}
                    </p>
                    <p className="text-xs text-slate-700 italic mt-0.5">
                      {d.title_en}
                    </p>
                  </div>
                </div>
                <p className="text-xs text-slate-600 mb-3 flex-1">{d.description}</p>
                <div className="flex items-center justify-between text-xs text-slate-700 mb-3">
                  <span className="inline-flex items-center gap-1">
                    <Sparkles className="h-3 w-3" /> v{d.version}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Calendar className="h-3 w-3" /> {d.last_updated}
                  </span>
                </div>
                <div className="flex gap-2">
                  {d.file_el_url && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-xs h-8"
                      onClick={() => downloadDeliverable(d.file_el_url, d.title_el)}
                      data-testid={`download-${d.id}-el`}
                    >
                      <Download className="h-3 w-3 mr-1" />
                      EL · {(d.file_el_filename || d.file_el_url).split('.').pop().toUpperCase().slice(0, 4)}
                    </Button>
                  )}
                  {d.file_en_url ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 border-blue-300 text-blue-700 hover:bg-blue-50 text-xs h-8"
                      onClick={() => downloadDeliverable(d.file_en_url, d.title_en)}
                      data-testid={`download-${d.id}-en`}
                    >
                      <Download className="h-3 w-3 mr-1" />
                      EN · {(d.file_en_filename || d.file_en_url).split('.').pop().toUpperCase().slice(0, 4)}
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled
                      className="flex-1 text-xs h-8 text-slate-700"
                      title="English version coming soon"
                    >
                      EN · soon
                    </Button>
                  )}
                </div>
                {user.role === 'administrator' && (
                  <AdminDeliverableButtons deliverable={d} onChanged={fetchDeliverables} />
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Quick Info */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <div className="flex items-start space-x-3">
            <Info className="h-5 w-5 text-blue-700 mt-0.5" />
            <div>
              <h3 className="font-medium text-blue-900">Guide Access Information</h3>
              <p className="text-sm text-blue-700 mt-1">
                {user.role === 'administrator' ? 
                  "As an administrator, you have access to all user guides including role-specific documentation." :
                  `As a ${user.role?.replace('_', ' ')}, you can access guides relevant to your role plus general platform documentation.`
                }
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* User Guides Grid */}
      {user.role === 'administrator' && (
        <div className="flex items-center justify-between pt-2">
          <h3 className="text-base font-semibold text-slate-900">User Guides</h3>
          <NewGuideButton onChanged={fetchGuides} />
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {guides.filter(isRelevantForUser).map((guide) => {
          const RoleIcon = getRoleIcon(guide.role);
          const roleColorClass = getRoleColor(guide.role);

          return (
            <Card key={guide.id} className="hover:shadow-lg transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-base mb-2">{guide.title}</CardTitle>
                    <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${roleColorClass}`}>
                      <RoleIcon className="h-3 w-3 mr-1" />
                      {guide.role === 'all' ? 'All Users' : guide.role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </div>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="pt-0">
                <p className="text-sm text-gray-700 mb-3 line-clamp-3">{guide.description}</p>
                
                <div className="flex items-center justify-between text-xs text-gray-700 mb-4">
                  <div className="flex items-center space-x-1">
                    <FileText className="h-3 w-3" />
                    <span>{guide.pages}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Calendar className="h-3 w-3" />
                    <span>Updated {guide.updated}</span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openGuide(guide, 'en')}
                      className="flex-1"
                      data-testid={`read-online-${guide.id}`}
                    >
                      <Eye className="h-3 w-3 mr-1" />
                      Read Online
                    </Button>
                    
                    <Button
                      size="sm"
                      onClick={() => downloadGuide(guide, 'txt', 'en')}
                      disabled={downloading === guide.id}
                      className="flex-1"
                      data-testid={`download-${guide.id}`}
                    >
                      <Download className="h-3 w-3 mr-1" />
                      {downloading === guide.id ? 'Downloading...' : 'Download'}
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => downloadGuide(guide, 'docx', 'en')}
                      disabled={downloading === guide.id}
                      className="text-green-700 border-green-300 hover:bg-green-50"
                    >
                      <FileDown className="h-3 w-3 mr-1" />
                      Word Format
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => downloadGuide(guide, 'markdown', 'en')}
                      disabled={downloading === guide.id}
                    >
                      <FileDown className="h-3 w-3 mr-1" />
                      Markdown
                    </Button>
                  </div>

                  {/* ── Greek companion row ── */}
                  <div className="pt-2 border-t border-dashed border-gray-200">
                    {guide.has_greek ? (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center text-xs text-emerald-700 font-medium">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Greek companion (EL) available
                          </div>
                          {guide.greek_stale && (
                            <span
                              className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-medium"
                              title="The English source has been updated since the Greek companion was generated."
                              data-testid={`stale-badge-${guide.id}`}
                            >
                              EL out of date
                            </span>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openGuide(guide, 'el')}
                            className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                            data-testid={`read-online-el-${guide.id}`}
                          >
                            <Languages className="h-3 w-3 mr-1" />
                            Read EL
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => downloadGuide(guide, 'docx', 'el')}
                            disabled={downloading === guide.id}
                            className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                            data-testid={`download-el-${guide.id}`}
                          >
                            <FileDown className="h-3 w-3 mr-1" />
                            Download EL · Word
                          </Button>
                        </div>
                        {user.role === 'administrator' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => translateGuideToGreek(guide)}
                            disabled={!!translatingGuides[guide.id]}
                            className="w-full text-xs text-gray-700 hover:text-emerald-700"
                            data-testid={`retranslate-el-${guide.id}`}
                          >
                            <Sparkles className={`h-3 w-3 mr-1 ${translatingGuides[guide.id] ? 'animate-pulse' : ''}`} />
                            {translatingGuides[guide.id] ? 'Re-translating…' : 'Re-translate to Greek (AI)'}
                          </Button>
                        )}
                      </div>
                    ) : (
                      user.role === 'administrator' ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => translateGuideToGreek(guide)}
                          disabled={!!translatingGuides[guide.id]}
                          className="w-full text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                          data-testid={`translate-el-${guide.id}`}
                        >
                          <Sparkles className={`h-3 w-3 mr-1 ${translatingGuides[guide.id] ? 'animate-pulse' : ''}`} />
                          {translatingGuides[guide.id] ? 'Translating to Greek…' : 'Translate to Greek (AI)'}
                        </Button>
                      ) : (
                        <p className="text-xs text-gray-700 italic flex items-center">
                          <Languages className="h-3 w-3 mr-1" />
                          Greek companion not yet generated
                        </p>
                      )
                    )}
                  </div>
                </div>
                {user.role === 'administrator' && (
                  <AdminGuideButtons guide={guide} onChanged={fetchGuides} />
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Guide Content Dialog */}
      <Dialog open={showGuideDialog} onOpenChange={setShowGuideDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <BookOpen className="h-5 w-5 text-blue-700" />
              <span>{selectedGuide?.title}</span>
            </DialogTitle>
            <DialogDescription>
              {selectedGuide?.description}
            </DialogDescription>
          </DialogHeader>
          
          <div className="mt-6">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div className="flex items-center gap-3">
                <div className="text-sm text-gray-700">
                  {selectedGuide?.pages} • Updated {selectedGuide?.updated}
                </div>
                {selectedGuide?.has_greek && (
                  <div className="inline-flex rounded-md border border-gray-200 bg-white p-0.5">
                    <button
                      type="button"
                      onClick={() => openGuide(selectedGuide, 'en')}
                      className={`px-2 py-0.5 text-xs rounded ${guideLang === 'en' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-50'}`}
                      data-testid="dialog-lang-en"
                    >
                      EN
                    </button>
                    <button
                      type="button"
                      onClick={() => openGuide(selectedGuide, 'el')}
                      className={`px-2 py-0.5 text-xs rounded ${guideLang === 'el' ? 'bg-emerald-700 text-white' : 'text-gray-700 hover:bg-gray-50'}`}
                      data-testid="dialog-lang-el"
                    >
                      EL
                    </button>
                  </div>
                )}
              </div>
              <div className="flex space-x-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => downloadGuide(selectedGuide, 'docx', guideLang)}
                  disabled={downloading === selectedGuide?.id}
                  className="text-green-700 border-green-300 hover:bg-green-50"
                >
                  <Download className="h-3 w-3 mr-1" />
                  Word Format
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => downloadGuide(selectedGuide, 'txt', guideLang)}
                  disabled={downloading === selectedGuide?.id}
                >
                  <Download className="h-3 w-3 mr-1" />
                  TXT
                </Button>
                <Button
                  size="sm"
                  onClick={() => downloadGuide(selectedGuide, 'markdown', guideLang)}
                  disabled={downloading === selectedGuide?.id}
                >
                  <FileDown className="h-3 w-3 mr-1" />
                  Markdown
                </Button>
              </div>
            </div>
            
            <div className="prose prose-sm max-w-none">
              <div 
                className="whitespace-pre-wrap font-mono text-sm bg-gray-50 p-4 rounded-lg border max-h-[60vh] overflow-y-auto"
                dangerouslySetInnerHTML={{ 
                  __html: formatMarkdownForDisplay(guideContent) 
                }}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserGuides;